import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  RadioGroup,
  WarningBanner,
  useConfirm,
} from "./shared/components";
import { NoDuesFormSections } from "./shared/NoDuesFormSections";
import {
  getNoDuesDeptDetail,
  submitNoDuesDeptClearance,
  saveNoDuesSavedItem,
  deleteNoDuesSavedItem,
  getNoDuesSavedItems,
  getResignationDetails,
  getNoDuesFormDetails,
  getEmployeeDetails,
  NoDuesDeptDetail,
  formatDate,
} from "./services/eSeparation.service";

interface ChecklistRow {
  key: string;
  item_name: string;
  amount_due: string;
  comments: string;
  savedId?: number;
  isEditing?: boolean;
}

let rowSeq = 0;
const newKey = () => `row_${Date.now()}_${rowSeq++}`;

// flattenDetail formats absent dates to "—", so a naive `a || b` fallback keeps
// the placeholder instead of using the profile value. Treat "—"/empty as blank.
const isBlank = (v: any) =>
  v === null || v === undefined || v === "" || v === "—";
const pick = (...vals: any[]): any => vals.find((v) => !isBlank(v)) ?? "";

const blankRow = (isEditing = true): ChecklistRow => ({
  key: newKey(),
  item_name: "",
  amount_due: "",
  comments: "",
  isEditing,
});

const fmt = (v: any): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
    return formatDate(v);
  }
  return String(v);
};

const NoDuesDeptAction: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const routes = all_routes;
  const { confirm, confirmDialog } = useConfirm();

  const state = (location.state as any) || {};
  const dispatchId: number = Number(state.dispatch_id || params.id);
  const headerDeptName: string = state.department_name || "Department";

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [detail, setDetail] = useState<NoDuesDeptDetail | null>(null);
  const [employee, setEmployee] = useState<any>(null);
  // The parent resignation / case, for the "View Resignation Form" button.
  const [resignationId, setResignationId] = useState<string>("");

  const [rows, setRows] = useState<ChecklistRow[]>([]);
  const [summary, setSummary] = useState("");
  const [decision, setDecision] = useState<"CLEARED" | "REJECTED">("CLEARED");
  const [shortNoticeAutoAdded, setShortNoticeAutoAdded] = useState(false);

  const isFinanceDept = headerDeptName.toLowerCase().includes("finance");

  const departmentId =
    detail?.dispatch?.department_id ?? Number(state.department_id) ?? 0;
  const requestId =
    detail?.dispatch?.request_id ?? state.request_id ?? "";
  const alreadyActioned =
    String(detail?.dispatch?.status ?? "").toUpperCase() !== "PENDING";

  const load = useCallback(async () => {
    if (!dispatchId) return;
    setLoading(true);
    try {
      const res = await getNoDuesDeptDetail(dispatchId);
      const data = res.data ?? null;
      setDetail(data);

      const reqId = data?.dispatch?.request_id ?? state.request_id ?? "";
      (async () => {
        try {
          const formSnap: any = (data as any)?.form || {};
          let ndRecord: any = null;
          if (reqId) {
            try {
              const ndRes = await getNoDuesFormDetails(String(reqId));
              ndRecord = ndRes.data ?? null;
            } catch {
              ndRecord = null;
            }
          }

          const candidateIds = Array.from(
            new Set(
              [
                ndRecord?.request_id,
                ndRecord?.orig_request_id,
                ndRecord?.resignation_request_id,
                formSnap?.request_id,
                String(reqId),
              ]
                .filter(Boolean)
                .map(String)
            )
          );
          let resignation: any = null;
          for (const cid of candidateIds) {
            const r = await getResignationDetails(cid);
            if (!r.data) continue;
            if (!resignation) resignation = r.data;
            if (
              !isBlank(r.data.reason) ||
              !isBlank(r.data.emp_group) ||
              r.data.emp_no ||
              r.data.doj
            ) {
              resignation = r.data;
              break;
            }
          }

          const empNo = pick(
            resignation?.emp_no,
            ndRecord?.emp_no,
            ndRecord?.details?.emp_no,
            formSnap?.emp_no
          );
          let profile: any = null;
          if (empNo) {
            try {
              const prof = await getEmployeeDetails(String(empNo));
              profile = prof.data ?? null;
            } catch {
              profile = null;
            }
          }

          const merged = {
            emp_name: pick(
              resignation?.emp_name,
              profile?.emp_name,
              ndRecord?.emp_name,
              ndRecord?.details?.emp_name,
              formSnap?.emp_name
            ),
            emp_no: pick(
              resignation?.emp_no,
              profile?.emp_no,
              ndRecord?.emp_no,
              ndRecord?.details?.emp_no,
              formSnap?.emp_no
            ),
            designation: pick(
              resignation?.designation,
              profile?.designation,
              ndRecord?.designation,
              ndRecord?.details?.designation,
              formSnap?.designation
            ),
            wing: pick(
              resignation?.wing,
              profile?.wing,
              ndRecord?.wing,
              ndRecord?.details?.wing,
              formSnap?.wing
            ),
            posting: pick(
              resignation?.posting,
              profile?.department,
              profile?.posting,
              ndRecord?.posting,
              ndRecord?.details?.department_name,
              formSnap?.department_name
            ),
            pay_scale: pick(
              resignation?.pay_scale,
              profile?.pay_scale,
              ndRecord?.pay_scale,
              ndRecord?.details?.pay_scale,
              formSnap?.pay_scale
            ),
            doj: pick(
              resignation?.doj,
              profile?.doj,
              ndRecord?.doj,
              ndRecord?.details?.doj,
              formSnap?.doj
            ),
            emp_group: pick(
              resignation?.emp_group,
              profile?.emp_group,
              ndRecord?.emp_group,
              ndRecord?.details?.emp_group,
              formSnap?.emp_group
            ),
            date_of_leaving: pick(
              ndRecord?.date_of_leaving,
              ndRecord?.details?.date_of_leaving,
              resignation?.notice_end_date,
              resignation?.date_of_leaving,
              resignation?.approved_wef,
              resignation?.desired_relieve_date,
              formSnap?.date_of_leaving
            ),
            reason: pick(
              ndRecord?.reason_for_leaving,
              ndRecord?.details?.reason,
              resignation?.reason,
              formSnap?.reason
            ),
            intimation_date: pick(
              ndRecord?.intimation_date,
              ndRecord?.details?.intimation_date,
              resignation?.intimation_date,
              formSnap?.intimation_date
            ),
            outstanding_loans:
              resignation?.outstanding_loans ||
              ndRecord?.outstanding_loans ||
              ndRecord?.details?.outstanding_loans ||
              formSnap?.outstanding_loans ||
              [],
            short_notice_amount: pick(
              ndRecord?.short_notice_amount,
              ndRecord?.details?.short_notice_amount,
              resignation?.short_notice_amount,
              formSnap?.short_notice_amount
            ),
            shortfall_days: pick(
              ndRecord?.shortfall_days,
              ndRecord?.details?.shortfall_days,
              resignation?.shortfall_days,
              formSnap?.shortfall_days
            ),
          };
          setEmployee(merged);
          setResignationId(String(resignation?.request_id || ndRecord?.orig_request_id || ""));
        } catch {
          // non-fatal
        }
      })();

      if (data?.checklist_items && data.checklist_items.length > 0) {
        setRows(
          data.checklist_items.map((i) => ({
            key: newKey(),
            item_name: i.item_name,
            amount_due: i.amount_due ? String(i.amount_due) : "",
            comments: i.comments || "",
            isEditing: false,
          }))
        );
      } else {
        const saved = data?.saved_items ?? [];
        setRows(
          saved.length > 0
            ? saved.map((s) => ({
                key: newKey(),
                item_name: s.item_name,
                amount_due: "",
                comments: "",
                savedId: s.id,
                isEditing: true,
              }))
            : [blankRow(true)]
        );
      }
      setSummary(data?.dispatch?.remarks || "");
    } catch {
      toast.error("Failed to load clearance detail");
    }
    setLoading(false);
  }, [dispatchId]);

  useEffect(() => {
    load();
  }, [load]);

  // When Finance dept loads and short notice amount is present, auto-inject it
  // into the checklist if no items have been saved/actioned yet.
  useEffect(() => {
    if (!employee || !isFinanceDept || shortNoticeAutoAdded || alreadyActioned) return;
    const shortAmount = Number(employee.short_notice_amount);
    if (shortAmount <= 0) return;
    // Only inject when the checklist currently has just a single blank row
    setRows((prev) => {
      const hasContent = prev.some((r) => r.item_name.trim());
      if (hasContent) return prev; // checklist already populated — don't touch
      const shortNoticeRow: ChecklistRow = {
        key: newKey(),
        item_name: "Short Notice Period Recovery (Early Relieving)",
        amount_due: String(shortAmount),
        comments: `Auto-calculated: (Basic Pay / 30) × ${employee.shortfall_days || 'N/A'} shortfall days`,
        isEditing: true,
      };
      // Replace the blank row with the pre-filled short notice row
      return [shortNoticeRow];
    });
    setShortNoticeAutoAdded(true);
  }, [employee, isFinanceDept, shortNoticeAutoAdded, alreadyActioned]);

  const updateRow = (key: string, patch: Partial<ChecklistRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const addRow = () => {
    setRows((prev) => [
      ...prev.map((r) => (r.item_name.trim() ? { ...r, isEditing: false } : r)),
      blankRow(true),
    ]);
  };

  const removeFromForm = (key: string) =>
    setRows((prev) => prev.filter((r) => r.key !== key));

  const saveForFuture = async (row: ChecklistRow) => {
    if (!row.item_name.trim()) {
      toast.error("Enter an item name before saving");
      return;
    }
    if (!departmentId) {
      toast.error("Department not resolved");
      return;
    }
    const res = await saveNoDuesSavedItem({
      department_id: departmentId,
      item_name: row.item_name.trim(),
      amount_due: Number(row.amount_due) || 0,
      comments: row.comments.trim(),
    });
    if (!res.success) {
      toast.error(res.message || "Failed to save item");
      return;
    }
    const saved = await getNoDuesSavedItems(departmentId);
    const match = (saved.data || []).find(
      (s) =>
        s.item_name.trim().toLowerCase() === row.item_name.trim().toLowerCase()
    );
    updateRow(row.key, { savedId: match?.id });
    toast.success("Item saved for future forms");
  };

  const removeFromSaved = async (row: ChecklistRow) => {
    if (!row.savedId) return removeFromForm(row.key);
    const ok = await confirm(
      "Stop showing this item by default on future No-Dues forms?",
      { title: "Remove from Saved", confirmLabel: "Remove", tone: "danger" }
    );
    if (!ok) return;
    const res = await deleteNoDuesSavedItem(row.savedId);
    if (res.success) {
      removeFromForm(row.key);
      toast.success("Removed from saved items");
    } else {
      toast.error(res.message || "Failed to remove saved item");
    }
  };

  const totalDue = rows.reduce(
    (sum, r) => sum + (Number(r.amount_due) || 0),
    0
  );

  const handleSubmit = async () => {
    const filled = rows.filter((r) => r.item_name.trim());
    if (decision === "REJECTED" && filled.length === 0 && !summary.trim()) {
      toast.error("Add at least one due item or a summary when flagging dues");
      return;
    }
    const ok = await confirm(
      decision === "CLEARED"
        ? "Confirm this department has no pending dues for the employee?"
        : `Confirm dues of ₹${totalDue.toLocaleString(
            "en-IN"
          )} for this department?`,
      {
        title: decision === "CLEARED" ? "Mark Cleared" : "Record Dues",
        confirmLabel: "Confirm",
        tone: decision === "CLEARED" ? "success" : "danger",
      }
    );
    if (!ok) return;
    setSubmitting(true);
    const res = await submitNoDuesDeptClearance({
      dispatch_id: dispatchId,
      status: decision,
      summary: summary.trim(),
      recovery_amount: totalDue,
      items: filled.map((r) => ({
        item_name: r.item_name.trim(),
        amount_due: Number(r.amount_due) || 0,
        comments: r.comments.trim(),
      })),
    });
    setSubmitting(false);
    if (res.success) {
      toast.success(
        decision === "CLEARED" ? "Clearance recorded" : "Dues recorded"
      );
      navigate(routes.Esepration.noDuesDeptClearance);
    } else {
      toast.error(res.message || "Failed to submit clearance");
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="text-center py-5">
            <div className="spinner-border text-secondary" role="status" />
            <p className="text-muted mt-3 mb-0">Loading clearance...</p>
          </div>
        </div>
      </div>
    );
  }

  const form = detail?.form ?? null;

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content pb-5">
        {/* Breadcrumbs & Page Header */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-1 small">
                <li className="breadcrumb-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(routes.Esepration.hub);
                    }}
                    className="text-decoration-none text-muted"
                  >
                    E-Separation
                  </a>
                </li>
                <li className="breadcrumb-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(routes.Esepration.noDuesDeptClearance);
                    }}
                    className="text-decoration-none text-muted"
                  >
                    No-Dues Clearance
                  </a>
                </li>
                <li className="breadcrumb-item active text-primary fw-medium" aria-current="page">
                  {headerDeptName} Clearance
                </li>
              </ol>
            </nav>
            <h4 className="fw-bold text-dark mb-0">No-Dues Clearance</h4>
          </div>
          <div className="d-flex align-items-center gap-2">
            {resignationId && (
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() =>
                  navigate(routes.Esepration.approvalDetail.replace(":id", resignationId), {
                    state: { request_id: resignationId, type: "Intimation", role: "Employee", viewOnly: true },
                  })
                }
              >
                <i className="ti ti-file-text me-1" />
                View Resignation Form
              </button>
            )}
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1" />
              Exit
            </button>
          </div>
        </div>

        {/* Sub-header Banner */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body py-3 px-4">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <span
                  className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: "#FBE9E2",
                    color: "#993C1D",
                  }}
                >
                  <i className="ti ti-clipboard-list" style={{ fontSize: 22 }} />
                </span>
                <div>
                  <div
                    style={{
                      fontSize: "17px",
                      fontWeight: 700,
                      color: "#344054",
                      lineHeight: "1.3",
                    }}
                  >
                    {headerDeptName} Clearance
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#667085",
                      lineHeight: "1.4",
                      marginTop: "2px",
                    }}
                  >
                    {requestId || "—"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 01: EMPLOYEE DETAILS */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  01
                </span>
                Employee Details
              </h5>
              <div className="text-muted small mt-1 ms-4 ps-2">
                Basic employee information from the separation record.
              </div>
            </div>
          </div>
          <div className="card-body p-4">
            {employee ? (
              <div className="row g-3">
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Name</label>
                  <input type="text" className="form-control bg-light" value={fmt(employee.emp_name)} readOnly />
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Employee No.</label>
                  <input type="text" className="form-control bg-light" value={fmt(employee.emp_no)} readOnly />
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Designation</label>
                  <input type="text" className="form-control bg-light" value={fmt(employee.designation)} readOnly />
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Wing</label>
                  <input type="text" className="form-control bg-light" value={fmt(employee.wing)} readOnly />
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Department</label>
                  <input type="text" className="form-control bg-light" value={fmt(employee.posting)} readOnly />
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Pay Scale</label>
                  <input type="text" className="form-control bg-light" value={fmt(employee.pay_scale)} readOnly />
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Date of Joining</label>
                  <input type="text" className="form-control bg-light" value={fmt(employee.doj)} readOnly />
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Employee Group</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={
                      employee.emp_group === "A"
                        ? "Executive"
                        : employee.emp_group === "B"
                        ? "Non-Executive"
                        : fmt(employee.emp_group)
                    }
                    readOnly
                  />
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Date of Leaving</label>
                  <input type="text" className="form-control bg-light" value={fmt(employee.date_of_leaving)} readOnly />
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="form-label fw-semibold text-secondary small mb-1">Date of Submission</label>
                  <input type="text" className="form-control bg-light" value={fmt(employee.intimation_date)} readOnly />
                </div>

                {Number(employee.short_notice_amount) > 0 && isFinanceDept && (
                  <div className="col-12 mt-1">
                    <div
                      className="p-3 rounded-3 d-flex align-items-start gap-3"
                      style={{ background: "#FFF3CD", border: "1px solid #FFC107" }}
                    >
                      <i className="ti ti-alert-triangle text-warning mt-1" style={{ fontSize: 20, flexShrink: 0 }} />
                      <div>
                        <div className="fw-bold text-dark mb-1" style={{ fontSize: 14 }}>
                          Short Notice Period Recovery Required
                        </div>
                        <div className="text-secondary small mb-1">
                          This employee resigned under <strong>short notice</strong> (early relieving). As per DMRC policy,
                          the shortfall in notice period salary must be recovered by Finance.
                        </div>
                        <div className="d-flex align-items-center gap-2 mt-2">
                          <span className="text-muted small">Recoverable Amount:</span>
                          <span className="fw-bold text-danger" style={{ fontSize: 16 }}>
                            ₹ {Number(employee.short_notice_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                          This amount has been pre-filled in the Department Checklist below for your confirmation.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="col-12 mt-2">
                  <label className="form-label fw-semibold text-secondary small mb-1">Reason for Leaving</label>
                  <div
                    className="p-3 rounded-3 bg-light text-dark small"
                    style={{ border: "1px solid #e9ecef", minHeight: 60, whiteSpace: "pre-wrap" }}
                  >
                    {fmt(employee.reason)}
                  </div>
                </div>
              </div>
            ) : (
              <WarningBanner
                type="info"
                message="Employee details are not available."
              />
            )}
          </div>
        </div>

        {/* SECTIONS 02-05: the employee's own submitted No-Dues form,
            rendered identically to how they filled it in. */}
        {form ? (
          <NoDuesFormSections form={form} startNumber={2} loansOverride={employee?.outstanding_loans} />
        ) : (
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
              <div>
                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                    style={{ width: 34, height: 34, fontSize: 15 }}
                  >
                    02
                  </span>
                  Employee No-Dues Details
                </h5>
              </div>
            </div>
            <div className="card-body p-4">
              <WarningBanner type="info" message="No-Dues form details are not available." />
            </div>
          </div>
        )}

        {/* SECTION 06: DEPARTMENT CHECKLIST */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  06
                </span>
                Department Checklist
              </h5>
              <div className="text-muted small mt-1 ms-4 ps-2">
                Add the items, amounts due and comments specific to this department.
              </div>
            </div>
          </div>
          <div className="card-body p-4">
            {alreadyActioned && (
              <WarningBanner
                type="info"
                message="This clearance has already been actioned and is shown read-only."
              />
            )}

            {rows.map((row, i) => {
              const isAdded = row.isEditing === false && Boolean(row.item_name.trim());

              if (isAdded) {
                return (
                  <div
                    key={row.key}
                    className="p-3 mb-3 border rounded-3 bg-white shadow-sm"
                    style={{ borderColor: "#198754", borderLeft: "5px solid #198754" }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}>
                          Item {i + 1}
                        </span>
                        <span
                          className="badge bg-success text-white px-2 py-1 d-inline-flex align-items-center"
                          style={{ fontSize: "12px", fontWeight: 600, borderRadius: "6px" }}
                        >
                          <i className="ti ti-check me-1" />
                          Added
                        </span>
                        {row.savedId ? (
                          <span
                            className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1"
                            style={{ fontSize: "12px", borderRadius: "6px" }}
                          >
                            <i className="ti ti-bookmark me-1" />
                            Saved
                          </span>
                        ) : null}
                      </div>

                      {!alreadyActioned && (
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary px-3"
                            onClick={() => updateRow(row.key, { isEditing: true })}
                            title="Edit Item"
                          >
                            <i className="ti ti-edit me-1" />
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeFromForm(row.key)}
                            title="Remove Item"
                          >
                            <i className="ti ti-trash me-1" />
                            Remove
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="row g-3">
                      <div className="col-lg-6 col-md-6 col-sm-12">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Item Name
                        </label>
                        <input
                          type="text"
                          className="form-control bg-light fw-bold text-dark"
                          value={row.item_name}
                          readOnly
                        />
                      </div>

                      <div className="col-lg-6 col-md-6 col-sm-12">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Amount Due (₹)
                        </label>
                        <input
                          type="text"
                          className="form-control bg-light fw-bold text-danger"
                          value={`₹ ${Number(row.amount_due || 0).toLocaleString("en-IN")}`}
                          readOnly
                        />
                      </div>

                      {row.comments && (
                        <div className="col-12">
                          <label className="form-label fw-semibold text-secondary small mb-1">
                            Comments
                          </label>
                          <div className="p-2 px-3 rounded-2 bg-light text-dark small border">
                            {row.comments}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={row.key}
                  className="p-3 mb-3 border rounded-3 bg-light"
                  style={{ borderColor: "#b6d4fe", borderLeft: "4px solid #0d6efd" }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-primary">Item {i + 1}</span>
                      <span className="badge bg-primary-subtle text-primary">Active Form</span>
                    </div>
                    {row.savedId ? (
                      <span className="badge d-inline-flex align-items-center gap-1 bg-primary-subtle text-primary border border-primary-subtle">
                        <i className="ti ti-bookmark" />
                        Saved
                      </span>
                    ) : null}
                  </div>

                  <div className="row g-3">
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <label className="form-label fw-semibold text-secondary small mb-1">Item Name</label>
                      <input
                        className="form-control bg-white"
                        placeholder="e.g. Laptop not returned"
                        value={row.item_name}
                        disabled={alreadyActioned}
                        onChange={(e) =>
                          updateRow(row.key, { item_name: e.target.value })
                        }
                      />
                    </div>

                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <label className="form-label fw-semibold text-secondary small mb-1">Amount Due (₹)</label>
                      <input
                        type="number"
                        className="form-control bg-white"
                        placeholder="0"
                        value={row.amount_due}
                        disabled={alreadyActioned}
                        onChange={(e) =>
                          updateRow(row.key, { amount_due: e.target.value })
                        }
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold text-secondary small mb-1">Comments</label>
                      <textarea
                        className="form-control bg-white"
                        rows={2}
                        placeholder="Any notes for this item..."
                        value={row.comments}
                        disabled={alreadyActioned}
                        onChange={(e) =>
                          updateRow(row.key, { comments: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  {!alreadyActioned && (
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3 pt-2 border-top">
                      <div className="d-flex gap-2">
                        {row.savedId ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeFromSaved(row)}
                            >
                              <i className="ti ti-bookmark-off me-1" />
                              Remove from saved
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => removeFromForm(row.key)}
                            >
                              <i className="ti ti-x me-1" />
                              Remove from form
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => saveForFuture(row)}
                            >
                              <i className="ti ti-bookmark me-1" />
                              Save for future
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => removeFromForm(row.key)}
                            >
                              <i className="ti ti-trash me-1" />
                              Remove
                            </button>
                          </>
                        )}
                      </div>

                      {row.item_name.trim() && (
                        <button
                          type="button"
                          className="btn btn-sm btn-success d-flex align-items-center gap-1"
                          onClick={() => updateRow(row.key, { isEditing: false })}
                        >
                          <i className="ti ti-check" />
                          Done
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {!alreadyActioned && (
              <button
                type="button"
                className="btn w-100 mb-3 d-flex align-items-center justify-content-center gap-2 py-2"
                style={{
                  border: "1px dashed #0d6efd",
                  borderRadius: 10,
                  color: "#0d6efd",
                  background: "#f0f7ff",
                }}
                onClick={addRow}
              >
                <i className="ti ti-circle-plus" />
                Add Item
              </button>
            )}

            <div className="d-flex justify-content-between align-items-center pt-3 border-top mt-3">
              <span style={{ fontSize: "16px", fontWeight: 600, color: "#1f2937" }}>
                Total Amount Due
              </span>
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: totalDue > 0 ? "#dc2626" : "#16a34a",
                }}
              >
                ₹ {Number(totalDue).toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        </div>

        {/* SECTION 07: SUMMARY & ACTION */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  07
                </span>
                Summary &amp; Action
              </h5>
            </div>
          </div>
          <div className="card-body p-4">
            <div className="mb-3">
              <label className="form-label fw-semibold text-secondary small mb-1">
                Summary / Comments
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Overall clearance summary..."
                value={summary}
                disabled={alreadyActioned}
                onChange={(e) => setSummary(e.target.value)}
              />
            </div>

            {!alreadyActioned && (
              <>
                <div className="mb-3">
                  <label className="form-label fw-semibold text-secondary small mb-2">
                    Decision
                  </label>
                  <RadioGroup
                    name="nd-dept-action-decision"
                    value={decision === "CLEARED" ? "yes" : "no"}
                    onChange={(v) =>
                      setDecision(v === "yes" ? "CLEARED" : "REJECTED")
                    }
                    options={[
                      { label: "Cleared", value: "yes" },
                      { label: "Dues Found", value: "no" },
                    ]}
                  />
                </div>

                <button
                  type="button"
                  className="btn text-white d-flex align-items-center justify-content-center gap-2 px-4 py-2"
                  style={{
                    background: decision === "CLEARED" ? "#049123" : "#C0282C",
                    borderRadius: 10,
                    height: 44,
                  }}
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <i
                      className={`ti ${
                        decision === "CLEARED"
                          ? "ti-circle-check"
                          : "ti-alert-circle"
                      }`}
                    />
                  )}
                  <span>{decision === "CLEARED" ? "Mark Cleared" : "Record Dues"}</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoDuesDeptAction;
